import React, { useEffect, useMemo, useState } from "react";
import { externalEventsApi } from "../services/externalPlatformApi";

const EMPTY_EVENT = {
  destination: "Ibiza",
  month: "May",
  event_date: "",
  venue: "",
  event_name: "",
  artists: "",
  time_slot: "",
  price: "",
  ticket_url: "",
  ticket_partner: "Skiddle",
  hlt_essential: false,
  active: true,
  sort_order: 0,
};

export default function AdminExternalEvents() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const grouped = useMemo(() => {
    return events.reduce((acc, event) => {
      const key = event.destination || "Other";
      acc[key] = acc[key] || [];
      acc[key].push(event);
      return acc;
    }, {});
  }, [events]);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoading(true);
    setError("");
    try {
      const payload = await externalEventsApi.list({ includeInactive: true, limit: 500 });
      setEvents(Array.isArray(payload?.events) ? payload.events : []);
    } catch {
      setError("Could not load external events. The backend endpoint may not be live yet.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function editEvent(event) {
    setEditingId(event.id);
    setForm({
      destination: event.destination || "Ibiza",
      month: event.month || "May",
      event_date: event.event_date || "",
      venue: event.venue || "",
      event_name: event.event_name || "",
      artists: event.artists || "",
      time_slot: event.time_slot || "",
      price: event.price || "",
      ticket_url: event.ticket_url || "",
      ticket_partner: event.ticket_partner || "Skiddle",
      hlt_essential: !!event.hlt_essential,
      active: event.active !== false,
      sort_order: event.sort_order || 0,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_EVENT);
  }

  async function saveEvent(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      sort_order: Number(form.sort_order || 0),
    };

    try {
      if (editingId) {
        await externalEventsApi.update(editingId, payload);
      } else {
        await externalEventsApi.create(payload);
      }
      resetForm();
      await loadEvents();
    } catch {
      setError("Could not save the event. Check required fields and backend availability.");
    } finally {
      setSaving(false);
    }
  }

  async function removeEvent(id) {
    if (!window.confirm("Remove this external event from the public site?")) return;
    try {
      await externalEventsApi.remove(id);
      await loadEvents();
    } catch {
      setError("Could not remove the event.");
    }
  }

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">External Events</h1>
            <p className="mt-1 text-slate-300">
              Manage Ibiza, Marbella, Malta, Amsterdam and partner event listings.
            </p>
          </div>
          <span className="badge bg-brand-600/20 text-slate-100">
            {events.length} listing{events.length === 1 ? "" : "s"}
          </span>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      <section className="grid gap-6 lg:grid-cols-[380px,1fr]">
        <form className="card space-y-4" onSubmit={saveEvent}>
          <div>
            <h2 className="text-xl font-semibold">
              {editingId ? "Edit external event" : "Add external event"}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              Paste the final Skiddle, Last Minute or venue affiliate URL into ticket URL.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Destination">
              <select
                className="input"
                value={form.destination}
                onChange={(e) => updateField("destination", e.target.value)}
              >
                {["Ibiza", "Marbella", "London", "Malta", "Amsterdam"].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </Field>
            <Field label="Month">
              <input
                className="input"
                value={form.month}
                onChange={(e) => updateField("month", e.target.value)}
                placeholder="May"
              />
            </Field>
          </div>

          <Field label="Event date">
            <input
              type="date"
              className="input"
              value={form.event_date}
              onChange={(e) => updateField("event_date", e.target.value)}
              required
            />
          </Field>

          <Field label="Venue">
            <input
              className="input"
              value={form.venue}
              onChange={(e) => updateField("venue", e.target.value)}
              required
            />
          </Field>

          <Field label="Event name">
            <input
              className="input"
              value={form.event_name}
              onChange={(e) => updateField("event_name", e.target.value)}
              required
            />
          </Field>

          <Field label="Artists">
            <input
              className="input"
              value={form.artists}
              onChange={(e) => updateField("artists", e.target.value)}
              placeholder="Carl Cox, ANTS, Black Coffee"
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Time">
              <input
                className="input"
                value={form.time_slot}
                onChange={(e) => updateField("time_slot", e.target.value)}
                placeholder="23:00 - late"
              />
            </Field>
            <Field label="Price">
              <input
                className="input"
                value={form.price}
                onChange={(e) => updateField("price", e.target.value)}
                placeholder="From GBP 40"
              />
            </Field>
          </div>

          <Field label="Ticket URL">
            <input
              className="input"
              value={form.ticket_url}
              onChange={(e) => updateField("ticket_url", e.target.value)}
              placeholder="https://..."
              required
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Partner">
              <input
                className="input"
                value={form.ticket_partner}
                onChange={(e) => updateField("ticket_partner", e.target.value)}
              />
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                className="input"
                value={form.sort_order}
                onChange={(e) => updateField("sort_order", e.target.value)}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.hlt_essential}
              onChange={(e) => updateField("hlt_essential", e.target.checked)}
            />
            HLT Essential badge
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => updateField("active", e.target.checked)}
            />
            Active on public site
          </label>

          <div className="flex gap-2">
            <button className="btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Add event"}
            </button>
            {editingId && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>

        <section className="space-y-4">
          {loading && <div className="card text-slate-300">Loading external events...</div>}
          {!loading && events.length === 0 && (
            <div className="card text-slate-300">No external events have been added yet.</div>
          )}
          {!loading &&
            Object.entries(grouped).map(([destinationName, items]) => (
              <div key={destinationName} className="card">
                <h2 className="text-xl font-semibold">{destinationName}</h2>
                <div className="mt-4 space-y-3">
                  {items.map((event) => (
                    <article
                      key={event.id}
                      className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap gap-2">
                            <span className="badge">{event.month}</span>
                            <span className="badge">{event.event_date}</span>
                            {!event.active && (
                              <span className="badge bg-red-500/20 text-red-200">Inactive</span>
                            )}
                            {event.hlt_essential && (
                              <span className="badge bg-brand-600/20 text-slate-100">
                                HLT Essential
                              </span>
                            )}
                          </div>
                          <h3 className="mt-2 text-lg font-semibold text-white">
                            {event.event_name}
                          </h3>
                          <p className="text-sm text-slate-300">
                            {event.venue}
                            {event.artists ? ` - ${event.artists}` : ""}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button className="btn-secondary" onClick={() => editEvent(event)}>
                            Edit
                          </button>
                          <button className="btn-secondary" onClick={() => removeEvent(event.id)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
        </section>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}
